import React, { useEffect, useState } from 'react';
import {
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Typography,
    Box,
    CircularProgress,
    Badge,
    Button
} from '@mui/material';
import {
    NotificationsActive as AlertIcon,
    CheckCircle as CheckIcon,
    Warning as WarningIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { notificationsService } from '../../../services/notificationsService';
import { navigateFromNotification } from '../../../utils/notificationNavigation';
import { Notification } from '../../../types';

// Notification types that require an action from the user
const ACTIONABLE_NOTIFICATION_TYPES = [
    'document_checker_required',
    'document_approver_required',
    'document_approval_required',
    'document_upload_required',
    'new_time_off_request',
    'project_assigned',
];

const ActionsRequiredWidget: React.FC = () => {
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchActions = async () => {
            try {
                // Fetch pending actions (uncompleted actionable notifications)
                // The backend now persists these until the action is actually completed
                const response = await notificationsService.getPendingActions();
                setNotifications(response.notifications);
                setLoading(false);
            } catch (err) {
                console.error('Failed to fetch actions', err);
                setError('Failed to load actions');
                setLoading(false);
            }
        };

        fetchActions();
    }, []);

    const getNotificationTitle = (type: string) => {
        switch (type) {
            case 'document_checker_required': return 'Review Required';
            case 'document_approver_required': return 'Approval Required';
            case 'document_approval_required': return 'Approval Required';
            case 'document_upload_required': return 'Upload Required';
            case 'document_rejected': return 'Re-upload Required';
            case 'new_time_off_request': return 'Time Off Request';
            case 'project_assigned': return 'Project Assigned';
            default: return 'Action Required';
        }
    };

    const handleNotificationClick = async (notification: Notification) => {
        // Mark as read only for informational notifications (like project assignment)
        // For actionable notifications, they will be marked as read by the backend when the action is completed
        if (!notification.isRead && notification.type === 'project_assigned') {
            try {
                await notificationsService.markAsRead(notification.id);
                setNotifications(prev =>
                    prev.map(n => n.id === notification.id ? { ...n, isRead: true } : n)
                );
            } catch (err) {
                console.error('Failed to mark notification as read:', err);
            }
        }

        try {
            await navigateFromNotification(notification, navigate);
        } catch (error) {
            console.error('Failed to navigate from notification:', error);
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} /></Box>;
    }

    if (error) {
        return <Typography color="error" variant="body2">{error}</Typography>;
    }

    if (notifications.length === 0) {
        return (
            <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                <CheckIcon sx={{ fontSize: 40, color: 'success.light', mb: 1 }} />
                <Typography variant="body2">You're all caught up!</Typography>
            </Box>
        );
    }

    return (
        <Box>
            <List dense>
                {notifications.slice(0, 5).map((notification) => (
                    <ListItem
                        key={notification.id}
                        button
                        onClick={() => handleNotificationClick(notification)}
                        sx={{
                            pl: 0,
                            borderLeft: '3px solid',
                            borderColor: 'warning.main',
                            mb: 1,
                            bgcolor: 'background.paper'
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: 36, ml: 1 }}>
                            <WarningIcon color="warning" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                            primary={getNotificationTitle(notification.type)}
                            secondary={notification.message}
                            primaryTypographyProps={{ variant: 'subtitle2', sx: { fontWeight: 600 } }}
                            secondaryTypographyProps={{ variant: 'caption', noWrap: true, display: 'block' }}
                        />
                    </ListItem>
                ))}
            </List>

        </Box>
    );
};

export default ActionsRequiredWidget;
